import React, { useMemo, useState } from 'react';
import type { BuffetMealPeriod, BuffetPackage } from '../../types';
import { BUFFET_MEAL_PERIODS, getBuffetPackages } from '../../lib/buffetPackages';

interface BuffetTypeSelectorProps {
  onSelect: (pkg: BuffetPackage) => void;
  onCancel: () => void;
  packages: BuffetPackage[];
}

const MEAL_PERIOD_EMOJI: Record<BuffetMealPeriod, string> = {
  breakfast: '🌅',
  lunch: '☀️',
  dinner: '🌙',
};

const BuffetTypeSelector: React.FC<BuffetTypeSelectorProps> = ({ onSelect, onCancel, packages }) => {
  const [mealPeriod, setMealPeriod] = useState<BuffetMealPeriod | null>(null);

  const packagesForPeriod = useMemo(
    () => (mealPeriod ? getBuffetPackages({ buffetPackages: packages }, mealPeriod) : []),
    [packages, mealPeriod],
  );

  const countsByPeriod = useMemo(() => {
    const counts: Record<BuffetMealPeriod, number> = { breakfast: 0, lunch: 0, dinner: 0 };
    for (const p of BUFFET_MEAL_PERIODS) {
      counts[p.id] = getBuffetPackages({ buffetPackages: packages }, p.id).length;
    }
    return counts;
  }, [packages]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto my-auto">
        <div className="p-4 border-b flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-800">
            {mealPeriod ? `${mealPeriod.charAt(0).toUpperCase() + mealPeriod.slice(1)} packages` : 'Select buffet'}
          </h2>
          <button type="button" onClick={onCancel} className="text-gray-500 hover:text-gray-800 text-2xl">
            ×
          </button>
        </div>

        <div className="p-6">
          {!mealPeriod ? (
            <>
              <p className="text-sm text-gray-600 mb-4 text-center">
                Choose a meal period, then pick a buffet package.
              </p>
              <div className="space-y-3">
                {BUFFET_MEAL_PERIODS.map(({ id, label }) => {
                  const count = countsByPeriod[id];
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setMealPeriod(id)}
                      className="w-full p-4 border-2 border-gray-200 rounded-lg hover:border-pink-400 hover:bg-pink-50 transition-colors text-left"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="text-3xl">{MEAL_PERIOD_EMOJI[id]}</span>
                          <div>
                            <div className="font-semibold text-gray-800">{label}</div>
                            <div className={`text-sm ${count > 0 ? 'text-gray-600' : 'text-yellow-600'}`}>
                              {count > 0 ? `${count} package${count === 1 ? '' : 's'}` : 'No packages configured'}
                            </div>
                          </div>
                        </div>
                        <div className="text-pink-600 font-semibold">→</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setMealPeriod(null)}
                className="text-sm text-pink-600 hover:text-pink-700 mb-3"
              >
                ← Back to meal periods
              </button>
              {packagesForPeriod.length === 0 ? (
                <p className="text-sm text-gray-600 text-center py-6">
                  No {mealPeriod} packages yet. Add them under{' '}
                  <span className="font-semibold">Menu → Buffet packages</span>.
                </p>
              ) : (
                <div className="space-y-3">
                  {packagesForPeriod.map((pkg) => (
                    <button
                      key={pkg.id}
                      type="button"
                      onClick={() => onSelect(pkg)}
                      className="w-full p-4 border-2 border-gray-200 rounded-lg hover:border-pink-400 hover:bg-pink-50 transition-colors text-left"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="font-semibold text-gray-800 truncate">{pkg.name}</div>
                          {pkg.description ? (
                            <div className="text-xs text-gray-500 mt-0.5 line-clamp-2">{pkg.description}</div>
                          ) : null}
                          <div className="text-sm text-gray-600 mt-1">${pkg.price.toFixed(2)} / person</div>
                        </div>
                        <div className="text-pink-600 font-semibold shrink-0">→</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <div className="p-4 border-t bg-gray-50 flex justify-end">
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-semibold"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default BuffetTypeSelector;
